import { useMemoize } from '@vueuse/core';
import { useNuxtApp } from 'nuxt/app';
import type { ApiInstance, LaravelApiResponse } from '~/types/plugins/api';
import type { MappedPrice, Price } from '~/types/pricing/Price';
import { PriceInterval, PriceProductKey } from '~/utils/enums/Price';

export const usePricing = () => {
  const { $apiClient } = useNuxtApp() as unknown as {
    $apiClient: ApiInstance;
  };
  const pricing = ref<Price[]>([]);
  const fetching = ref(false);
  const interval = ref(PriceInterval.MONTH);
  const error = ref(null);

  const memoizeFetch = useMemoize(async (interval: PriceInterval) => {
    try {
      error.value = null;
      const response = await $apiClient.get<LaravelApiResponse<Price[]>>(
        'stripe/subscription/plans',
        {
          params: {
            interval,
          },
        },
      );
      return response.data;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      memoizeFetch.clear();
      error.value =
        err?.response?.data?.message ||
        err.message ||
        'Failed to fetch pricing';
      return [];
    }
  });

  async function fetchPricing() {
    fetching.value = true;
    pricing.value = await memoizeFetch(interval.value);
    fetching.value = false;
  }

  const planOptions = computed<MappedPrice[]>(() => {
    return pricing.value
      .map((p) => {
        return {
          id: p.id,
          name: p.name,
          description: p.metadata.description,
          type: p.product_key,
          features: JSON.parse(p.metadata.items),
          amount: '$' + p.amount,
          interval: p.interval,
          priceSubtitle: p.metadata.price_subtitle?.trim(),
        };
      })
      .concat([
        {
          id: PriceProductKey.ENTERPRISE,
          name: 'As an Enterprise',
          description: 'Negotiable for large organisations.',
          type: PriceProductKey.ENTERPRISE,
          features:
            "A tailored plan to fit your organisation's unique needs, plus dedicated enterprise support.",
          amount: '',
          interval: '',
          priceSubtitle: undefined,
        },
      ]);
  });

  return {
    fetchPricing,
    fetching,
    pricing,
    interval,
    planOptions,
  };
};
